/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { logger } from '@/services/logger'
import i18n, { type MessageLanguages } from '@/i18n'
import '@/types/chrome' // Import chrome types for global Window interface extension

// Helper function to get system theme preference (moved up for early use)
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false)
  const currentLanguage = ref('en')
  // Initialize theme with system theme to prevent flash of wrong theme
  const theme = ref<'light' | 'dark'>(getSystemTheme())
  const sidebarOpen = ref(false)

  // Getters
  const isDarkTheme = computed(() => theme.value === 'dark')

  // Actions
  function setLoading(loading: boolean) {
    isLoading.value = loading
  }

  function setLanguage(language: string) {
    // Validate language
    if (!['en', 'fr', 'es'].includes(language)) {
      logger.warn('Invalid language provided, defaulting to English', { language }, 'app')
      language = 'en'
    }

    currentLanguage.value = language
    localStorage.setItem('noorwriter-locale', language)

    // Update i18n locale - fix: use .value for ref assignment
    if (i18n.global.locale.value !== language) {
      i18n.global.locale.value = language as MessageLanguages
    }

    // Sync language change with Chrome extension
    syncLanguageWithExtension(language)
  }

  function setTheme(newTheme: 'light' | 'dark' | 'system') {
    if (newTheme === 'system') {
      theme.value = getSystemTheme()
    } else {
      theme.value = newTheme
    }

    // Apply theme to document
    applyThemeToDocument()

    // Store theme preference locally (will be synced with user profile separately)
    localStorage.setItem('noorwriter-theme', newTheme)
  }

  function toggleTheme() {
    const currentTheme = localStorage.getItem('noorwriter-theme') || 'light'

    let newTheme: 'light' | 'dark' | 'system'
    if (currentTheme === 'light') {
      newTheme = 'dark'
    } else if (currentTheme === 'dark') {
      newTheme = 'system'
    } else {
      newTheme = 'light'
    }

    setTheme(newTheme)
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  function setSidebarOpen(open: boolean) {
    sidebarOpen.value = open
  }

  // Apply theme to document
  function applyThemeToDocument() {
    if (typeof document !== 'undefined') {
      const root = document.documentElement

      // Remove all theme classes first
      root.classList.remove('light', 'dark')

      // Add the current theme class
      root.classList.add(theme.value)

      // Also set a data attribute for CSS that might need it
      root.setAttribute('data-theme', theme.value)
    }
  }

  // Initialize language with proper priority order
  function initializeLanguage() {
    try {
      // Priority order: stored locale preference > browser language > default 'en'
      const storedLocale = localStorage.getItem('noorwriter-locale')

      let languageToApply = 'en'

      if (storedLocale && ['en', 'fr', 'es'].includes(storedLocale)) {
        languageToApply = storedLocale
      } else {
        // Fallback to browser language
        const browserLang = navigator.language?.split('-')[0]
        if (browserLang && ['en', 'fr', 'es'].includes(browserLang)) {
          languageToApply = browserLang
        }
      }

      // Update app store language
      currentLanguage.value = languageToApply

      // Update i18n locale to ensure synchronization
      if (i18n.global.locale.value !== languageToApply) {
        i18n.global.locale.value = languageToApply as MessageLanguages
      }

      // Store the language preference
      localStorage.setItem('noorwriter-locale', languageToApply)

      logger.debug('Language initialized', { language: languageToApply }, 'app')
    } catch (error) {
      logger.warn('Failed to initialize language:', { error: error instanceof Error ? error.message : String(error) }, 'app')
      // Fallback to English
      currentLanguage.value = 'en'
      i18n.global.locale.value = 'en'
      localStorage.setItem('noorwriter-locale', 'en')
    }
  }

  // Initialize theme with proper priority order
  function initializeTheme() {
    try {
      // Priority order: user database preference > local preference > system theme
      const userTheme = localStorage.getItem('noorwriter-user-theme') as 'light' | 'dark' | 'system' | null
      const localTheme = localStorage.getItem('noorwriter-theme') as 'light' | 'dark' | 'system' | null

      let themeToApply: 'light' | 'dark'

      if (userTheme && ['light', 'dark', 'system'].includes(userTheme)) {
        // User has a saved theme preference, use it
        if (userTheme === 'system') {
          themeToApply = getSystemTheme()
        } else {
          themeToApply = userTheme
        }
      } else if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
        // No user theme preference, check local storage
        if (localTheme === 'system') {
          themeToApply = getSystemTheme()
        } else {
          themeToApply = localTheme
        }
      } else {
        // No stored preferences, use system theme
        themeToApply = getSystemTheme()
      }

      theme.value = themeToApply
      applyThemeToDocument()

      // Listen for system theme changes
      if (typeof window !== 'undefined' && window.matchMedia) {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        mediaQuery.addEventListener('change', (e) => {
          // Only apply system theme changes if user preference is 'system' or no preference is set
          const currentUserTheme = localStorage.getItem('noorwriter-user-theme')
          const currentLocalTheme = localStorage.getItem('noorwriter-theme')

          if ((currentUserTheme === 'system') ||
              (!currentUserTheme && currentLocalTheme === 'system') ||
              (!currentUserTheme && !currentLocalTheme)) {
            theme.value = e.matches ? 'dark' : 'light'
            applyThemeToDocument()
          }
        })
      }
    } catch (error) {
      logger.warn('Failed to initialize theme', { error: String(error) }, 'app')
      // Fallback to system theme
      theme.value = getSystemTheme()
      applyThemeToDocument()
    }
  }

  // Sync theme with user settings (called from Settings component)
  function syncThemeWithUser(userTheme: 'light' | 'dark' | 'system') {
    if (userTheme === 'system') {
      theme.value = getSystemTheme()
    } else {
      theme.value = userTheme
    }

    applyThemeToDocument()

    // Store user theme preference (this will be synced with user profile)
    localStorage.setItem('noorwriter-user-theme', userTheme)
  }

  // Sync language changes with Chrome extension
  function syncLanguageWithExtension(language: string) {
    try {
      // Check if we're in a browser environment and Chrome extension API is available
      if (typeof window !== 'undefined' && window.chrome?.runtime?.sendMessage) {
        // Extension ID - this should match the actual extension ID
        // TODO: Make this configurable via environment variables
        const extensionId = 'oieeijmjdfimebgclcijdicdpkhcgboo'

        window.chrome.runtime.sendMessage(
          extensionId,
          {
            action: 'setLanguage',
            language: language
          },
          (_response: unknown) => {
            if (window.chrome?.runtime?.lastError) {
              // Extension not available or not responding - this is normal
              logger.debug('Extension not available for language sync', {}, 'app')
            } else {
              logger.debug('Language synced to extension', { language }, 'app')
            }
          }
        )
      }
    } catch (error) {
      // Silently fail - extension sync is optional
      logger.debug('Extension language sync failed:', { error: error instanceof Error ? error.message : String(error) }, 'app')
    }
  }

  return {
    // State
    isLoading,
    currentLanguage,
    theme,
    sidebarOpen,

    // Getters
    isDarkTheme,

    // Actions
    setLoading,
    setLanguage,
    setTheme,
    toggleTheme,
    toggleSidebar,
    setSidebarOpen,
    initializeLanguage,
    initializeTheme,
    syncThemeWithUser,
    applyThemeToDocument,
    syncLanguageWithExtension,
  }
})
